import { useEffect, useMemo, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { adminUsersApi } from '../api/adminUsersApi';
import {
  Button,
  DiscoveryToolbar,
  LoadError,
  LoadSkeleton,
  Modal,
  PreviewChip,
  SectionHeading,
} from '../components/ui';
import { TextField } from '../components/forms';
import { PACKAGE_KEYS, PACKAGE_LABELS } from '../lib/accessLevel';
import { VERIFIED_SIGNUP_CREDITS } from '../lib/credits';
import { UNLIMITED_FAIR_USE } from '../data/fairUse';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import {
  type AdminPublisherRelease,
  parseAdminEntitlementAction,
  type AdminUserAction,
  type AdminUserDetail,
  type AdminUsersPage,
} from '../types/adminUsers';

const LIMIT = 20;
const rowStyle = {
  border: `1px solid ${c.border}`,
  borderRadius: 10,
  background: c.surface1,
  padding: 14,
};
const message = (error: unknown) =>
  error instanceof Error ? error.message : 'The request could not be completed.';
const readableStatus = (value: string) =>
  value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const readableDate = (value: string) => {
  if (!value) return 'Not set';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};
const joinedDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Unknown' : parsed.toLocaleDateString();
};
const joinedTime = (value: string) => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};
const PURCHASE_PLATFORM_LABELS = {
  jvzoo: 'JVZoo',
  launchpadjv: 'Launchpad JV',
  gumroad: 'Gumroad',
  admin: 'Admin',
} as const;

function PublisherReleaseDetails({ release }: { release: AdminPublisherRelease }) {
  return (
    <details style={{ ...rowStyle, padding: 0 }}>
      <summary
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          padding: 12,
        }}
      >
        <span>
          <strong style={{ display: 'block', fontSize: 13 }}>
            {release.title || 'Untitled release'}
          </strong>
          <span style={{ color: c.text2, fontSize: 11 }}>
            {release.artistName || 'No artist'} · {release.releaseType} · {release.songs.length}{' '}
            {release.songs.length === 1 ? 'track' : 'tracks'}
          </span>
        </span>
        <span style={{ flexShrink: 0, fontSize: 12, color: c.text2 }}>
          {readableStatus(release.status)} · {release.workflowProgress}%
        </span>
      </summary>
      <div style={{ borderTop: `1px solid ${c.border}`, padding: 12 }}>
        <div
          aria-label="Workflow progress"
          style={{ height: 6, borderRadius: 999, overflow: 'hidden', background: c.surface2 }}
        >
          <div
            style={{
              width: `${release.workflowProgress}%`,
              height: '100%',
              background: release.status === 'failed' ? c.danger : c.accent,
            }}
          />
        </div>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(110px, auto) 1fr',
            gap: '7px 14px',
            margin: '12px 0',
            fontSize: 12,
          }}
        >
          <dt>Workflow</dt>
          <dd style={{ margin: 0 }}>{readableStatus(release.status)}</dd>
          <dt>Provider review</dt>
          <dd style={{ margin: 0 }}>{release.providerReviewStatus || 'Not reported'}</dd>
          <dt>Provider delivery</dt>
          <dd style={{ margin: 0 }}>{release.providerDeliveryStatus || 'Not reported'}</dd>
          <dt>Provider release ID</dt>
          <dd style={{ margin: 0 }}>{release.externalId ?? 'Not assigned'}</dd>
          <dt>UPC / barcode</dt>
          <dd style={{ margin: 0 }}>{release.barcode || 'Not assigned'}</dd>
          <dt>Release date</dt>
          <dd style={{ margin: 0 }}>{release.releaseDate || 'Not set'}</dd>
          <dt>Genre / language</dt>
          <dd style={{ margin: 0 }}>
            {release.genre || 'Not set'} · {release.language || 'Not set'}
          </dd>
          <dt>Explicit</dt>
          <dd style={{ margin: 0 }}>{release.explicit ? 'Yes' : 'No'}</dd>
          <dt>Writer / contributor</dt>
          <dd style={{ margin: 0 }}>{release.writerContributor || 'Not set'}</dd>
          <dt>Rights attested</dt>
          <dd style={{ margin: 0 }}>
            {release.rightsAttested === undefined
              ? 'Not recorded'
              : release.rightsAttested
                ? 'Yes'
                : 'No'}
          </dd>
          <dt>Selected outlets</dt>
          <dd style={{ margin: 0 }}>{release.outletKeys.join(', ') || 'None'}</dd>
          <dt>Created</dt>
          <dd style={{ margin: 0 }}>{readableDate(release.createdAt)}</dd>
          <dt>Last updated</dt>
          <dd style={{ margin: 0 }}>{readableDate(release.updatedAt)}</dd>
        </dl>
        {release.failureReason && (
          <p role="alert" style={{ color: c.danger, fontSize: 12 }}>
            Failure: {release.failureReason}
          </p>
        )}
        <p style={{ margin: '14px 0 7px', fontWeight: 600, fontSize: 12 }}>Songs</p>
        <ol style={{ display: 'grid', gap: 6, margin: 0, paddingLeft: 24, fontSize: 12 }}>
          {release.songs.length ? (
            release.songs.map((song) => (
              <li key={song.id} style={{ color: song.missing ? c.danger : c.text1 }}>
                {song.title} {song.length && `· ${song.length}`} · {readableStatus(song.status)}
                {song.missing && <span style={{ display: 'block' }}>ID: {song.id}</span>}
              </li>
            ))
          ) : (
            <li style={{ color: c.text2 }}>No songs are attached to this release.</li>
          )}
        </ol>
        <p style={{ margin: '14px 0 7px', fontWeight: 600, fontSize: 12 }}>Outlet delivery</p>
        {release.platforms.length ? (
          <div style={{ display: 'grid', gap: 5, fontSize: 12 }}>
            {release.platforms.map((platform) => (
              <div
                key={platform.outletKey}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}
              >
                <span>{platform.outletName}</span>
                <span style={{ color: c.text2 }}>
                  {platform.status || 'Not reported'}
                  {platform.updatedAt ? ` · ${readableDate(platform.updatedAt)}` : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, color: c.text2, fontSize: 12 }}>
            No outlet delivery reports are stored.
          </p>
        )}
      </div>
    </details>
  );
}

function CreateUser({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actor = useAuthStore((state) => state.user);
  const canConfirm = actor?.authProvider !== 'google';

  async function submit() {
    if (!email.trim() || !password || !adminPassword || busy || !canConfirm) return;
    setBusy(true);
    setError('');
    try {
      const created = await adminUsersApi.create(email.trim(), password, adminPassword);
      setPassword('');
      setAdminPassword('');
      onCreated(created.user.id);
    } catch (err) {
      setError(message(err));
      setPassword('');
      setAdminPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Create user"
      width={500}
      onClose={busy ? undefined : onClose}
      closeOnEscape={!busy}
      closeOnOverlayClick={!busy}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p style={{ margin: '10px 0 16px', color: c.text2, fontSize: 12 }}>
          This creates a verified password account with app access and {VERIFIED_SIGNUP_CREDITS}{' '}
          creation credits that can sign in immediately. Package entitlements can be granted
          separately.
        </p>
        <TextField
          label="User email"
          type="email"
          value={email}
          onChange={setEmail}
          disabled={busy}
          required
          marginBottom={12}
        />
        <TextField
          label="New user password"
          type="password"
          value={password}
          onChange={setPassword}
          disabled={busy}
          required
          marginBottom={12}
        />
        <TextField
          label="Your administrator password"
          type="password"
          value={adminPassword}
          onChange={setAdminPassword}
          disabled={busy || !canConfirm}
          required
          marginBottom={12}
        />
        {!canConfirm && (
          <p role="status" style={{ fontSize: 12, color: c.text2 }}>
            Your account uses Google sign-in. Set an administrator password before creating users.
          </p>
        )}
        {error && (
          <p role="alert" style={{ color: c.danger, fontSize: 12 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={busy || !canConfirm || !email.trim() || !password || !adminPassword}
          >
            {busy ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EmailUser({
  detail,
  onClose,
  onSent,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  onSent: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const draft = { subject: subject.trim(), heading: heading.trim(), message: body.trim() };
  const complete = Boolean(draft.subject && draft.heading && draft.message);

  function edit(setter: (value: string) => void, value: string) {
    setter(value);
    setPreviewHtml('');
    setError('');
  }

  async function preview() {
    if (!complete || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await adminUsersApi.previewEmail(detail.user.id, draft);
      setPreviewHtml(result.html);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!previewHtml || busy) return;
    setBusy(true);
    setError('');
    try {
      await adminUsersApi.sendEmail(detail.user.id, draft);
      onSent();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Email user"
      width={720}
      onClose={busy ? undefined : onClose}
      closeOnEscape={!busy}
      closeOnOverlayClick={!busy}
    >
      <p style={{ margin: '10px 0 16px', color: c.text2, fontSize: 12 }}>
        Compose a branded email and review the exact template before sending it. The recipient
        greeting and Lyrixsa AI Team sign-off are added automatically.
      </p>
      <TextField
        label="Recipient"
        value={detail.user.email}
        onChange={() => undefined}
        readOnly
        marginBottom={12}
      />
      <TextField
        label="Subject"
        value={subject}
        onChange={(value) => edit(setSubject, value)}
        disabled={busy}
        required
        marginBottom={12}
      />
      <TextField
        label="Email heading"
        value={heading}
        onChange={(value) => edit(setHeading, value)}
        disabled={busy}
        required
        marginBottom={12}
      />
      <TextField
        label="Message"
        value={body}
        onChange={(value) => edit(setBody, value)}
        multiline
        rows={8}
        disabled={busy}
        required
        marginBottom={12}
      />
      {previewHtml && (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600 }}>Email preview</p>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            sandbox=""
            style={{
              display: 'block',
              width: '100%',
              minHeight: 480,
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              background: '#101114',
            }}
          />
        </div>
      )}
      {error && (
        <p role="alert" style={{ color: c.danger, fontSize: 12 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        {previewHtml ? (
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setPreviewHtml('')}>
              Edit email
            </Button>
            <Button disabled={busy} onClick={() => void send()}>
              {busy ? 'Sending…' : 'Send email'}
            </Button>
          </>
        ) : (
          <Button disabled={busy || !complete} onClick={() => void preview()}>
            {busy ? 'Loading preview…' : 'Preview email'}
          </Button>
        )}
      </div>
    </Modal>
  );
}

function UserDetail({
  id,
  onClose,
  onChanged,
  onDeleted,
  onEmail,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  onEmail: (detail: AdminUserDetail) => void;
}) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [releases, setReleases] = useState<AdminPublisherRelease[] | null>(null);
  const [releasesLoading, setReleasesLoading] = useState(true);
  const [releasesError, setReleasesError] = useState('');
  const [releasesRevision, setReleasesRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [action, setAction] = useState<AdminUserAction | null>(null);
  const [password, setPassword] = useState('');
  const passwordInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [deleting, setDeleting] = useState(false);
  const actor = useAuthStore((state) => state.user);
  const canConfirm = actor?.authProvider !== 'google';
  const isSelf = actor?.id === id;
  useEffect(() => {
    if (action || deleting) passwordInput.current?.focus();
  }, [action, deleting]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void adminUsersApi
      .detail(id)
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((err: unknown) => {
        if (active) setError(message(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, revision]);

  useEffect(() => {
    let active = true;
    setReleasesLoading(true);
    setReleasesError('');
    void adminUsersApi
      .publisherReleases(id)
      .then((value) => {
        if (active) setReleases(value);
      })
      .catch((err: unknown) => {
        if (active) setReleasesError(message(err));
      })
      .finally(() => {
        if (active) setReleasesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, releasesRevision]);

  async function confirm() {
    if (!action || !password || busy) return;
    const entitlementAction = parseAdminEntitlementAction(action);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const updated = await adminUsersApi.act(id, action, password);
      setDetail(updated);
      setNotice(
        action === 'promote'
          ? 'Administrator access granted. The user must sign in again to open the shared library.'
          : action === 'suspend'
            ? 'Access suspended. Purchased products are preserved but locked until restored.'
            : action === 'restore'
              ? 'Access restored. The user can sign in and use their purchased products again.'
              : entitlementAction?.enabled
                ? 'Entitlement granted. The user can refresh their app to load the update.'
                : 'Entitlement revoked. Access is blocked immediately by the server.'
      );
      setAction(null);
      onChanged();
    } catch (err) {
      setError(message(err));
    } finally {
      setPassword('');
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!password || busy || isSelf) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminUsersApi.remove(id, password);
      setPassword('');
      onDeleted();
    } catch (err) {
      setError(message(err));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  const selectedEntitlementAction = parseAdminEntitlementAction(action);

  return (
    <Modal
      open
      title="User details"
      headerAction={
        detail ? (
          <Button variant="secondary" disabled={busy} onClick={() => onEmail(detail)}>
            <Mail size={14} />
            Mail
          </Button>
        ) : undefined
      }
      width={760}
      onClose={busy ? undefined : onClose}
      closeOnEscape={!busy}
      closeOnOverlayClick={!busy}
    >
      {loading ? (
        <LoadSkeleton rows={3} label="Loading user details…" />
      ) : !detail ? (
        <LoadError
          message={error || 'Could not load user.'}
          onRetry={() => setRevision((value) => value + 1)}
        />
      ) : (
        <>
          <p style={{ margin: '12px 0 4px', fontWeight: 600 }}>
            {detail.user.name || 'Unnamed user'}
          </p>
          <p style={{ margin: '0 0 12px', color: c.text2, overflowWrap: 'anywhere' }}>
            {detail.user.email}
          </p>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '8px 16px',
              fontSize: 12,
            }}
          >
            <dt>Role</dt>
            <dd style={{ margin: 0 }}>
              {detail.user.isAdmin ? 'Admin' : 'User'} · Level {detail.level}
            </dd>
            <dt>Sign-in type</dt>
            <dd style={{ margin: 0 }}>
              {detail.user.authProvider === 'google' ? 'OAuth (Google)' : 'Password'}
            </dd>
            <dt>Email</dt>
            <dd style={{ margin: 0 }}>
              {detail.user.emailVerifiedAt ? 'Verified' : 'Not verified'}
            </dd>
            <dt>Joined</dt>
            <dd style={{ margin: 0 }}>{joinedDate(detail.joinedAt)}</dd>
            <dt>App access</dt>
            <dd style={{ margin: 0 }}>
              {detail.user.suspendedAt
                ? `Suspended ${new Date(detail.user.suspendedAt).toLocaleDateString()}`
                : detail.appAccess
                  ? 'Granted'
                  : 'Purchase required'}
            </dd>
            <dt>Purchase platform</dt>
            <dd style={{ margin: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {detail.purchasePlatforms.length > 0
                ? detail.purchasePlatforms.map((platform) => (
                    <PreviewChip key={platform}>{PURCHASE_PLATFORM_LABELS[platform]}</PreviewChip>
                  ))
                : 'None recorded'}
            </dd>
            <dt>Creation credits</dt>
            <dd style={{ margin: 0 }}>
              {detail.credits.used}/{detail.credits.total} used
            </dd>
            <dt>Publisher credits</dt>
            <dd style={{ margin: 0 }}>
              {detail.publisherCredits.used}/{detail.publisherCredits.total} used
            </dd>
          </dl>
          <p style={{ fontSize: 12, color: c.text2 }}>
            Level counts currently owned packages.
            {detail.user.isAdmin &&
              ' This admin uses the shared workspace; the packages below belong to their individual account.'}
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {PACKAGE_KEYS.map((key) => (
              <div
                key={key}
                style={{
                  ...rowStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: 10,
                }}
              >
                <span style={{ fontSize: 12 }}>{PACKAGE_LABELS[key]}</span>
                {detail.entitlements[key] ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: c.success }}>Active</span>
                    <Button
                      variant="secondary"
                      disabled={busy || !canConfirm}
                      aria-label={`Revoke ${PACKAGE_LABELS[key]}`}
                      onClick={() => {
                        setDeleting(false);
                        setAction(`revoke:${key}`);
                        setPassword('');
                        setError('');
                        setNotice('');
                      }}
                    >
                      Revoke
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={busy || !canConfirm}
                    aria-label={`Grant ${PACKAGE_LABELS[key]}`}
                    onClick={() => {
                      setDeleting(false);
                      setAction(key);
                      setPassword('');
                      setError('');
                      setNotice('');
                    }}
                  >
                    Grant
                  </Button>
                )}
              </div>
            ))}
          </div>
          <details style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', padding: '6px 0' }}>
              <strong style={{ fontSize: 13 }}>Publisher releases</strong>
              <span
                style={{ display: 'block', margin: '4px 0 0 18px', color: c.text2, fontSize: 12 }}
              >
                Read-only release and delivery data for support debugging.
              </span>
            </summary>
            <div style={{ marginTop: 12 }}>
              {!releasesLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <Button variant="ghost" onClick={() => setReleasesRevision((value) => value + 1)}>
                    Refresh releases
                  </Button>
                </div>
              )}
              {releasesLoading ? (
                <LoadSkeleton rows={2} label="Loading Publisher releases…" />
              ) : releasesError ? (
                <LoadError
                  message={releasesError}
                  onRetry={() => setReleasesRevision((value) => value + 1)}
                />
              ) : releases?.length ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {releases.map((release) => (
                    <PublisherReleaseDetails key={release.id} release={release} />
                  ))}
                </div>
              ) : (
                <p style={{ color: c.text2, fontSize: 12 }}>This user has no Publisher releases.</p>
              )}
            </div>
          </details>
          {!detail.user.isAdmin && (
            <div style={{ marginTop: 14 }}>
              <Button
                variant="secondary"
                style={detail.user.suspendedAt ? undefined : { color: c.danger }}
                disabled={busy || !canConfirm}
                onClick={() => {
                  setDeleting(false);
                  setAction(detail.user.suspendedAt ? 'restore' : 'suspend');
                  setPassword('');
                  setError('');
                  setNotice('');
                }}
              >
                {detail.user.suspendedAt ? 'Restore user access' : 'Suspend user access'}
              </Button>
              <p style={{ fontSize: 12, color: c.text2 }}>
                {detail.user.suspendedAt
                  ? 'Restoring access unlocks the products this user already owns.'
                  : 'Suspension signs the user out and locks every purchased product without deleting purchases.'}
              </p>
            </div>
          )}
          {!detail.user.isAdmin && (
            <div style={{ marginTop: 14 }}>
              <Button
                disabled={busy || !canConfirm || !detail.user.emailVerifiedAt}
                onClick={() => {
                  setDeleting(false);
                  setAction('promote');
                  setPassword('');
                  setError('');
                  setNotice('');
                }}
              >
                Upgrade to admin
              </Button>
              {!detail.user.emailVerifiedAt && (
                <p style={{ fontSize: 12, color: c.text2 }}>
                  Email verification is required before promotion.
                </p>
              )}
            </div>
          )}
          {!canConfirm && (
            <p role="status" style={{ fontSize: 12, color: c.text2 }}>
              Your account uses Google sign-in. Set an administrator password before granting
              access.
            </p>
          )}
          <div style={{ marginTop: 14 }}>
            <Button
              variant="secondary"
              style={{ color: c.danger }}
              disabled={busy || !canConfirm || isSelf}
              onClick={() => {
                setDeleting(true);
                setAction(null);
                setPassword('');
                setError('');
                setNotice('');
              }}
            >
              Delete user
            </Button>
            {isSelf && (
              <p style={{ fontSize: 12, color: c.text2 }}>
                You cannot delete the administrator account you are currently using.
              </p>
            )}
          </div>
          {deleting && (
            <form
              style={{ ...rowStyle, marginTop: 16, borderColor: c.danger }}
              onSubmit={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              <p style={{ marginTop: 0, fontWeight: 600, fontSize: 13, color: c.danger }}>
                Permanently delete this user?
              </p>
              <p style={{ fontSize: 12, color: c.text2 }}>
                This permanently removes {detail.user.email} and all associated active data. A
                read-only record is retained in the restricted deletion archive. Audio becomes
                inaccessible immediately, is permanently deleted after 60 days, and the account
                cannot be restored.
              </p>
              <TextField
                inputRef={passwordInput}
                label="Your administrator password"
                type="password"
                value={password}
                onChange={setPassword}
                disabled={busy}
                required
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="submit"
                  style={{ background: c.dangerFill, color: '#fff' }}
                  disabled={busy || !password}
                >
                  {busy ? 'Deleting…' : 'Delete user permanently'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setDeleting(false);
                    setPassword('');
                    setError('');
                  }}
                >
                  Cancel deletion
                </Button>
              </div>
            </form>
          )}
          {action && (
            <form
              style={{ ...rowStyle, marginTop: 16 }}
              onSubmit={(event) => {
                event.preventDefault();
                void confirm();
              }}
            >
              <p style={{ fontWeight: 600, fontSize: 13 }}>
                {action === 'promote'
                  ? 'Confirm administrator access'
                  : action === 'suspend'
                    ? 'Confirm access suspension'
                    : action === 'restore'
                      ? 'Confirm access restoration'
                      : `${selectedEntitlementAction?.enabled ? 'Grant' : 'Revoke'} ${
                          PACKAGE_LABELS[selectedEntitlementAction!.key]
                        }`}
              </p>
              <p style={{ fontSize: 12, color: c.text2 }}>
                {action === 'promote'
                  ? 'This user will be able to edit the shared admin library and manage users. Their current sessions will be signed out.'
                  : action === 'suspend'
                    ? 'The user will be signed out. All purchased products and unlocked areas will remain recorded but inaccessible until restored.'
                    : action === 'restore'
                      ? 'The user will regain access to all products and areas they previously purchased.'
                      : !selectedEntitlementAction?.enabled
                        ? selectedEntitlementAction?.key === 'unlimited'
                          ? 'This ends the monthly Unlimited allowance. Purchased top-up credits and Publisher Credits are preserved.'
                          : selectedEntitlementAction?.key === 'publisher'
                            ? 'This removes Publisher access. Existing Publisher Credits are preserved.'
                            : 'This removes the selected entitlement and blocks its protected features.'
                        : action === 'unlimited'
                          ? `Includes a one-year term, ${UNLIMITED_FAIR_USE.monthlyCredits} monthly creation credits and 25 Publisher Credits.`
                          : action === 'publisher'
                            ? 'Includes 10 Publisher Credits.'
                            : 'This grants the selected entitlement to this user.'}
              </p>
              <TextField
                inputRef={passwordInput}
                label="Your administrator password"
                type="password"
                value={password}
                onChange={setPassword}
                disabled={busy}
                required
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="submit" disabled={busy || !password}>
                  {busy ? 'Confirming…' : 'Confirm action'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setAction(null);
                    setPassword('');
                    setError('');
                  }}
                >
                  Cancel action
                </Button>
              </div>
            </form>
          )}
          {error && (
            <p role="alert" style={{ color: c.danger, fontSize: 12 }}>
              {error}
            </p>
          )}
          {notice && (
            <p role="status" style={{ color: c.success, fontSize: 12 }}>
              {notice}
            </p>
          )}
        </>
      )}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button variant="secondary" disabled={busy} onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

export default function AdminUsers() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<AdminUsersPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [emailUser, setEmailUser] = useState<AdminUserDetail | null>(null);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void adminUsersApi
      .list({ query, page, limit: LIMIT })
      .then((result) => {
        if (!active) return;
        if (page > 1 && !result.items.length) {
          setPage(Math.max(1, Math.ceil(result.total / LIMIT)));
          return;
        }
        setData(result);
      })
      .catch((err: unknown) => {
        if (active) setError(message(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query, page, revision]);
  const refresh = () => setRevision((value) => value + 1);
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / LIMIT));
  const users = useMemo(
    () =>
      [...(data?.items ?? [])].sort(
        (a, b) =>
          joinedTime(b.joinedAt) - joinedTime(a.joinedAt) || a.user.id.localeCompare(b.user.id)
      ),
    [data?.items]
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <SectionHeading
          as="h2"
          title="Users"
          subtitle="Manage user access and package entitlements"
          marginBottom={0}
        />
        <Button onClick={() => setCreating(true)}>Create user</Button>
      </div>
      {notice && (
        <p role="status" style={{ color: c.success, fontSize: 12 }}>
          {notice}
        </p>
      )}
      <DiscoveryToolbar
        query={search}
        onQueryChange={setSearch}
        refreshing={loading}
        onRefresh={refresh}
        searchLabel="Search users"
        refreshLabel="Refresh users"
        placeholder="Search users by name or email"
        marginBottom={12}
      />
      {error && <LoadError message={error} onRetry={refresh} />}
      {loading && !data ? (
        <LoadSkeleton rows={4} label="Loading users…" />
      ) : (
        <>
          <div
            aria-busy={loading}
            data-testid="admin-user-list"
            style={{ display: 'grid', gap: 8 }}
          >
            {users.map(({ user, level, joinedAt }) => (
              <button
                type="button"
                key={user.id}
                onClick={() => setSelectedId(user.id)}
                style={{
                  ...rowStyle,
                  textAlign: 'left',
                  color: c.text1,
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {user.name || 'Unnamed user'}
                    {user.isAdmin ? ' · Admin' : ''}
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      flexShrink: 0,
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        borderRadius: 999,
                        padding: '3px 9px',
                        fontSize: 11,
                        background: c.accentSoft,
                        color: c.accent,
                      }}
                    >
                      level {level}
                    </span>
                    <span style={{ fontSize: 11, color: c.text3, whiteSpace: 'nowrap' }}>
                      Joined {joinedDate(joinedAt)}
                    </span>
                  </span>
                </div>
                <p
                  style={{
                    margin: '6px 0 0',
                    fontSize: 12,
                    color: c.text2,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {user.email}
                </p>
              </button>
            ))}
          </div>
          {!loading && !error && !users.length && (
            <p style={{ fontSize: 12, color: c.text2 }}>No users match this search.</p>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button
              variant="secondary"
              disabled={loading || page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <span style={{ fontSize: 12, color: c.text2 }}>
              Page {page} of {pages} · {data?.total ?? 0} users
            </span>
            <Button
              variant="secondary"
              disabled={loading || page >= pages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
      {selectedId && (
        <UserDetail
          key={selectedId}
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={refresh}
          onDeleted={() => {
            setSelectedId(null);
            setNotice('User deleted successfully.');
            refresh();
          }}
          onEmail={(detail) => {
            setSelectedId(null);
            setEmailUser(detail);
          }}
        />
      )}
      {emailUser && (
        <EmailUser
          detail={emailUser}
          onClose={() => setEmailUser(null)}
          onSent={() => {
            setEmailUser(null);
            setNotice(`Email sent to ${emailUser.user.email}.`);
          }}
        />
      )}
      {creating && (
        <CreateUser
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelectedId(id);
            refresh();
          }}
        />
      )}
    </div>
  );
}
